import type { KeyValueStore } from '@elara/client-core/lib/key-value-store';

/**
 * Web `KeyValueStore` implementation backed by `localStorage` (ELARA-691).
 *
 * All access is wrapped: a missing global (SSR, hardened/locked-down contexts)
 * or a thrown access error (private mode, disabled storage, quota) degrades to
 * absence/no-op rather than crashing the caller — honouring the port's
 * never-throws contract.
 *
 * This file is web-only and must not be imported by `@elara/mobile`; React
 * Native composes its own MMKV-backed `KeyValueStore` instead.
 */
export function createWebKeyValueStore(): KeyValueStore {
  return {
    get(key) {
      try {
        if (typeof localStorage === 'undefined') return undefined;
        return localStorage.getItem(key) ?? undefined;
      } catch {
        return undefined;
      }
    },
    set(key, value) {
      try {
        if (typeof localStorage === 'undefined') return;
        localStorage.setItem(key, value);
      } catch {
        // Ignore storage errors (private mode, quota, disabled storage).
      }
    },
    remove(key) {
      try {
        if (typeof localStorage === 'undefined') return;
        localStorage.removeItem(key);
      } catch {
        // Ignore storage errors.
      }
    },
  };
}
